import { APIScope, FMSType } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import * as React from "react";

import { useHasPermission } from "../../../Helpers/Permissions/useHasPermission";
import FMSModel from "../../../Models/FMS/FMS.Model";
// import { Member } from "../../../Models/Member/Member.Current"; // ENG-2439: needed when DoorSwap/StorageCommander/Storage360 are re-enabled
import { useSwivlNavigate } from "../../../Models/NavBar/useSwivlNavigate";
import "./FMSIntegrationsView.scss";

function FMSAddButton(props: { type: FMSType; image: string; title: string; isAddingFMS: boolean | undefined; canEdit: boolean }) {
  return (
    <div
      className={`FMSAddButton ${!props.canEdit ? "disabled" : ""}`}
      onClick={() => {
        if (!props.isAddingFMS && props.canEdit) {
          FMSModel.addFMS(props.type);
        }
      }}
      style={!props.canEdit ? { cursor: "not-allowed", opacity: 0.5 } : {}}
      title={!props.canEdit ? "You do not have permission to add FMS integrations" : ""}
    >
      <img src={props.image} />
      <div className="hoverState">{props.isAddingFMS ? <Spinner /> : props.title}</div>
    </div>
  );
}

export default function FMSIntegrationsView() {
  const { isLoading, FMSs, isAddingFMS } = FMSModel.useState();
  const navigate = useSwivlNavigate();
  const canEditFMS = useHasPermission(APIScope.FMS_EDIT);

  const navigateToFMS = (id: string) => {
    navigate(`/fms/${id}`);
  };

  React.useEffect(() => {
    if (!FMSs) {
      FMSModel.load(false);
    }
  }, [FMSs]);

  if (isLoading) {
    return (
      <div className="FMSIntegrationsView">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="FMSIntegrationsView">
      <div className="ActiveIntegrations">
        <h2>Add FMS</h2>
        <div className="FMSAddButtons">
          <FMSAddButton
            type={FMSType.SiteLink}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-sitelink.png"
            title="+ ADD SITELINK"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.StorEdge}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-storEdge.png"
            title="+ ADD STOREDGE"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.SSM}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-ssm.png"
            title="+ ADD SSM"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.SSMCloud}
            image="https://swivl-cdn.s3-us-west-1.amazonaws.com/FMSLogos/app-ssm-rest.png"
            title="+ ADD SSM Cloud"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.Tenant}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-tenant.png"
            title="+ ADD TENANT"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          {/* ENG-2439: DoorSwap, StorageCommander, Storage360 hidden — not yet integrated with v3 Studio. Restore when integrations are complete. */}
          {/* <FMSAddButton
            type={FMSType.DoorSwap}
            image="https://swivl-cdn.s3-us-west-1.amazonaws.com/app-doorswap.png"
            title="+ ADD DOORSWAP"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.StorageCommander}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-storage-commander.png"
            title="+ ADD STORAGE COMMANDER"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          /> */}
          <FMSAddButton
            type={FMSType.QuikStor}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-quikstor.png"
            title="+ ADD QuikStor"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          {/* ENG-2439: Storage360 hidden — not yet integrated with v3 Studio. */}
          {/* {Member.isSwivlAdmin ? (
            <FMSAddButton
              type={FMSType.Storage360}
              image="https://swivl-cdn.s3-us-west-1.amazonaws.com/app-storage-360.png"
              title="+ ADD Storage360"
              isAddingFMS={isAddingFMS}
              canEdit={canEditFMS}
            />
          ) : null} */}
          <FMSAddButton
            type={FMSType.Cubby}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-cubby.png"
            title="+ ADD CUBBY"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.Monument}
            image="https://swivl-cdn.s3.us-west-1.amazonaws.com/app-monument.png"
            title="+ ADD MONUMENT"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
          <FMSAddButton
            type={FMSType.Yardi}
            image="https://swivl-cdn.s3-us-west-1.amazonaws.com/app-yardi.png"
            title="+ ADD YARDI"
            isAddingFMS={isAddingFMS}
            canEdit={canEditFMS}
          />
        </div>

        <div className="mt-12">
          <h2>Active Integrations</h2>
        </div>
        <div className="ActiveIntegrationsList">
          {FMSs &&
            FMSs.map((fms, index) => {
              return (
                <div className="ActiveIntegrationsItem" key={fms.id}>
                  <img className="icon" src={`https://swivl-cdn.s3.us-west-1.amazonaws.com/fms-icons/${fms.type}-icon.png`} />
                  <div className="name">{fms.name}</div>
                  {canEditFMS && (
                    <button className="edit" onClick={() => navigateToFMS(fms.id?.toString())}>
                      EDIT
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
